import React, { FC } from 'react';
import { DropdownMenu, Icon, Switch, Tooltip } from '@kontentino/ui';
import { usePostComposer } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import { PseudoPostTypesUtils } from 'app/modules/posts/utils/pseudoPostType';
import { PseudoPostTypeOption } from 'app/modules/posts/constants/pseudoPostTypeProperties';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import clsx from 'clsx';
import { Attachments } from 'types/Attachments';
import { PseudoPostType } from 'constants/post';
import useSetState from 'utils/hooks/useSetState';
import { processPostAttachments } from './utils/attachments';
import { useMediaUploadStatus } from 'components/posts/detail/composer/providers/MediaUploadStatusProvider';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { useTranslation } from 'react-i18next';

const SWITCH_TYPES_LIMIT = 5;

const PostComposerPseudoTypeSwitch: FC = () => {
  const { isUploading } = useMediaUploadStatus();
  const [attachmentsByPseudoType, setAttachmentsByPseudoType] =
    useSetState<Record<PseudoPostType, Attachments>>();

  const { form, setForm, setOptions } = usePostComposer();
  const { t } = useTranslation();

  const pseudoPostTypeOptions = [
    ...PseudoPostTypesUtils.getOptionsByPageType(form.page.type),
  ].slice(0, SWITCH_TYPES_LIMIT);
  const dropdownOptions = [
    ...PseudoPostTypesUtils.getOptionsByPageType(form.page.type),
  ].slice(SWITCH_TYPES_LIMIT);

  function handlePseudoTypeClick(pseudoPost: PseudoPostTypeOption) {
    setAttachmentsByPseudoType({
      [form.pseudoType]: form.attachments,
    });

    setForm((prev) => {
      const processedAttachmentsCards = processPostAttachments({
        pseudoPost,
        form,
        attachmentsByPseudoType,
      });

      const { type: pseudoPostType, subtype: pseudoPostSubType } =
        pseudoPost.getTypeAndSubtype({
          attachments: {
            ...processedAttachmentsCards,
            cards: processedAttachmentsCards,
          },
        });

      return {
        pseudoType: pseudoPost.key,
        type: pseudoPostType,
        subtype: pseudoPostSubType,
        attachments: {
          ...prev.attachments,
          cards: processedAttachmentsCards,
          firstComment: PseudoPostTypesUtils.supportsFirstComment(
            prev.page.type,
            pseudoPost.key,
          )
            ? prev.attachments.firstComment
            : undefined,
        },
      };
    });

    setOptions({
      canAppendLinkFromText: true,
    });
  }

  return (
    <Switch.List data-name="posts-composer_post-type-switch">
      {pseudoPostTypeOptions.map((pseudoType) => {
        const active = pseudoType.key === form.pseudoType;

        return (
          <Switch.Tab
            key={pseudoType.key}
            data-name={`posts-composer_post-type-switch_${pseudoType.key}`}
            data-cy={`posts-composer_post-type-switch_${pseudoType.key}`}
            onClick={() => handlePseudoTypeClick(pseudoType)}
            active={active}
            disabled={isUploading}
            className={clsx({
              'tw-cursor-wait tw-opacity-50': isUploading,
            })}
          >
            <Icon icon={pseudoType.icon} />
            {pseudoType.label}
            {pseudoType.helpText && (
              <Tooltip content={pseudoType.helpText(t)}>
                <Icon
                  icon={faCircleInfo}
                  size="sm"
                  className={clsx({
                    'tw-text-grayscale-100': !active,
                  })}
                />
              </Tooltip>
            )}
          </Switch.Tab>
        );
      })}
      {dropdownOptions.length > 0 && (
        <>
          <div className="tw-h-[28px] tw-w-[1px] tw-bg-grayscale-30" />
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Switch.Tab
                data-name="posts-composer_post-type-switch_more"
                data-cy="posts-composer_post-type-switch_more"
                className="!tw-borderl-l-1 !tw-w-[28px] tw-flex-none tw-cursor-pointer tw-border-l-black tw-outline-0"
                active={dropdownOptions
                  .map((option) => option.key)
                  .includes(form.pseudoType)}
              >
                <Icon icon={faChevronDown} />
              </Switch.Tab>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="tw-z-[99999] tw-w-[160px]">
                {dropdownOptions.map((pseudoType) => (
                  <DropdownMenu.Item
                    key={pseudoType.key}
                    selected={pseudoType.key === form.pseudoType}
                    data-name={`posts-composer_post-type-switch_${pseudoType.key}`}
                    data-cy={`posts-composer_post-type-switch_${pseudoType.key}`}
                    onClick={() => handlePseudoTypeClick(pseudoType)}
                  >
                    <Icon icon={pseudoType.icon} />
                    {pseudoType.label}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </>
      )}
    </Switch.List>
  );
};
export default PostComposerPseudoTypeSwitch;
